import { useEffect, useState, type CSSProperties, type FC, type MouseEventHandler, type ReactNode } from "react";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import { useBackgroundColor } from "@tennr/frontend/context";
import "./Button.css";

const InlineSVG = ({ path }: { path: string }) => {
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);

  useEffect(() => {
    fetch(path)
      .then((res) => res.text())
      .then(setSvgMarkup)
      .catch(console.error);
  }, [path]);

  return svgMarkup ? (
    <div dangerouslySetInnerHTML={{ __html: svgMarkup }} />
  ) : (
    <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFhAJ/wlseKgAAAABJRU5ErkJggg=="/>
  );
}

export type ButtonProps = {
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  iconPath?: string;
  iconPos?: "left" | "right";
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  borderRadius?: string | number;
  backdropFilter?: string;
  duration?: number;
  delay?: number;
  text?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  dontAnimateIntro?: boolean;
  ctaPlus?: boolean; // Whether to add a plus icon to the button
  isExpanded?: boolean;
  staticBackgroundColor?: string; // background value override that will not change with background
  staticTextColor?: string; // text color value override that will not change with background
  staticFractalColor?: string; // fractal color value override that will not change with background
  isCard?: boolean; // don't apply normal button fractals to these
  // default color overrides:
  backgroundColorLight?: string;
  backgroundColorDark?: string;
  textColorLight?: string;
  textColorDark?: string;
  fractalColorLight?: string;
  fractalColorDark?: string;
};

export const Button: FC<ButtonProps> = ({
  width,
  height,
  minWidth = 214,
  minHeight = 60,
  backdropFilter = "blur(5px)",
  borderRadius = "0",
  children,
  text,
  style,
  className = "",
  duration = 0.15,
  delay = 0,
  dontAnimateIntro,
  ctaPlus = false,
  isExpanded = false,
  backgroundColorLight = "#EEDFD1",
  backgroundColorDark = "#9B4E45",
  textColorLight = "#5F4037",
  textColorDark = "#FBFBF9",
  fractalColorLight = "#EEDFD1",
  fractalColorDark = "#DCB4B1",
  staticBackgroundColor,
  staticTextColor,
  staticFractalColor,
  isCard = false,
  iconPos,
  iconPath,
  ...rest
}) => {
  const { currentColor } = useBackgroundColor();

  // Simple luminance check - currentColor is [r, g, b] with values 0-1
  const isLightBackground = currentColor
    ? 0.2126 * currentColor[0] + 0.7152 * currentColor[1] + 0.0722 * currentColor[2] > 0.5
    : true;

  // Show light button colors if background is dark, show dark button colors if background is light
  const currentBackgroundColor = staticBackgroundColor ? staticBackgroundColor : isLightBackground ? backgroundColorDark : backgroundColorLight;
  const currentTextColor = staticTextColor ? staticTextColor : isLightBackground ? textColorDark : textColorLight;
  const currentFractalColor = staticFractalColor ? staticFractalColor : isLightBackground ? fractalColorDark : fractalColorLight;

  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  function useFractalAnimation({
    hovered, clicked, distance
  }: {
    hovered: boolean;
    clicked: boolean;
    distance: number,
  }) {
    const controls = useAnimation();

    useEffect(() => {
      if (clicked) {
        controls.start({
          x: 0,
          y: 0,
          opacity: 0,
          transition: {
            duration: 0.167,
            ease: [0.84, 0.00, 0.16, 1.00],
          },
        });
      } else if (hovered) {
        controls.start({
          x: distance,
          y: distance,
          opacity: 0.25,
          transition: {
            duration: 0.5,
            ease: [0.00, 0.59, 0.40, 1.00]
          }
        });
      } else {
        controls.start({
          x: 0,
          y: 0,
          opacity: 0,
          transition: {
            duration: 0.5,
            ease: [0.84, 0.00, 0.16, 1.00],
          }
        });
      }
    }, [hovered, clicked, distance, controls])
    return controls
  };

  const fractal1Animation = useFractalAnimation({ hovered, clicked, distance: 4 });
  const fractal2Animation = useFractalAnimation({ hovered, clicked, distance: 8.75 });
  const fractal3Animation = useFractalAnimation({ hovered, clicked, distance: 13 });


  return (
    <motion.div
      className={`button-container  ${className}`}
      initial={dontAnimateIntro ? false : { opacity: 0 }}
      animate={{
        opacity: 1,
      }}
      transition={{ type: "tween", ease: "backInOut", duration, delay }}
      style={{
        ...(minWidth !== undefined ? { minWidth } : {}),
        ...(minHeight !== undefined ? { minHeight } : {}),
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
      }}
    >
      {/* Fractals/shadows */}
      {!isCard && 
        <>
          <motion.div
            style={{
              borderRadius: 0,
              zIndex: 2,
              background: currentFractalColor,
              mixBlendMode: "multiply",
            }}
            className="button-background-layer"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={fractal1Animation}
          />
          <motion.div
            style={{
              borderRadius: 0,
              zIndex: 0,
              background: currentFractalColor,
              opacity: 0.5,
              mixBlendMode: "multiply",
            }}
            className="button-background-layer"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={fractal2Animation}
          />
          <motion.div
            style={{
              borderRadius: 0,
              zIndex: -1,
              background: currentFractalColor,
              opacity: 0.5,
              mixBlendMode: "multiply",
            }}
            className="button-background-layer"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={fractal3Animation}
          />
        </> 
      }
      
      {/* Main button face */}
      <motion.button
        className={`gradient-button`}
        style={{
          borderRadius,
          backdropFilter,
          zIndex: 2,
          ...style,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseDown={() => setClicked(true)}
        onMouseUp={() => setClicked(false)}
        onTouchStart={() => setClicked(true)}
        onTouchEnd={() => setClicked(false)}
        {...rest}
      >
        <motion.div
          className="button-base-layer"
          initial={false}
          animate={{
            background: currentBackgroundColor,
          }}
          transition={{
            type: "tween",
            ease: "linear",
            duration,
            delay,
          }}
          style={{
            borderRadius,
            zIndex: 1,
            mixBlendMode: "multiply",
          }}
        />
        <AnimatePresence>
          {/* Content */}
          <motion.div
            className={`button-content  ${ctaPlus && "button-content-cta-plus"}`}
            initial={dontAnimateIntro ? false : { opacity: 0 }}
            animate={{
              opacity: 1,
            }}
            exit={dontAnimateIntro ? undefined : { opacity: 0 }}
            transition={{
              type: "tween",
              ease: "linear",
              duration,
              delay: 0,
              stiffness: 300,
              damping: 30,
            }}
          >
            <div className="button-children-container">{children}</div>
            {text && (
              <motion.div
                className={`button-text icon-${(undefined !== iconPos) ? iconPos : ''}`}
                initial={dontAnimateIntro ? false : { opacity: 0 }}
                animate={{
                  opacity: 1,
                  color: currentTextColor,
                }}
                exit={dontAnimateIntro ? undefined : { opacity: 0 }}
                transition={{
                  type: "tween",
                  ease: "linear",
                  duration,
                  delay,
                  stiffness: 300,
                  damping: 30,
                }}
              >
                {iconPath && <InlineSVG{...{path:iconPath}}/>}
                <span>{text}</span>
              </motion.div>
            )}
            {ctaPlus && <motion.div className="button-plus" style={{ color: currentTextColor }}>{isExpanded ? "–" : "+"}</motion.div>}
          </motion.div>
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
};
